import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useDashboardData } from "../hooks/useDashboardData";
import { MobileBottomNav } from "../components/MobileBottomNav";
import { CaptureCard } from "../components/CaptureCard";
import { TrashCaptureCard } from "../components/TrashCaptureCard";
import { CaptureEditDialog } from "../components/CapturePreviewDialog";
import { NewTagDialog } from "../components/NewTagDialog";
import { NewFolderDialog } from "../components/NewFolderDialog";
import { AddTagDialog } from "../components/AddTagDialog";
import { BulkAddTagDialog } from "../components/BulkAddTagDialog";
import { SendEmailModal } from "../components/SendEmailModal";
import { ShareLinkDialog } from "../components/ShareLinkDialog";
import { MergeLayoutDialog } from "../components/MergeLayoutDialog";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { ImageSquare, Plus, UploadSimple, PaperPlaneTilt, Tag as TagIcon,
  Trash, CaretDown, CaretRight, FilePdf, LinkSimple, SignOut, Camera, Recycle, ArrowCounterClockwise, DeviceMobile, NotePencil, Stack,
  SortAscending, SortDescending, FolderSimple, FolderPlus, CalendarBlank, ClockCounterClockwise, Star,
} from "@phosphor-icons/react";
import { downloadCapturesPdf } from "../lib/pdf";
import { ALL_CAPTURES_FILTER, TODAY_FILTER } from "../lib/captureFilters";
import { ModuleNavBar } from "../components/ModuleNavBar";

export default function MobileDashboard() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const d = useDashboardData();
  const isTrash = d.viewMode === "trash";
  const [activeTab, setActiveTab] = useState("all");
  const [tagsSheetOpen, setTagsSheetOpen] = useState(false);
  const [meSheetOpen, setMeSheetOpen] = useState(false);
  const [uploadSheetOpen, setUploadSheetOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [expandedMobileFolderIds, setExpandedMobileFolderIds] = useState([]);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "tags") setTagsSheetOpen(true);
    if (tab === "me") setMeSheetOpen(true);
    if (tab === "all") d.selectTag(null);
  };

  const handleMergeClick = () => {
    if (d.selectedCaptures.length < 2) {
      toast.error(t("dashboard.mergeNeedsMinError"));
      return;
    }
    if (d.selectedCaptures.some((c) => c.type !== "image")) {
      toast.error(t("dashboard.mergeNeedsImageOnlyError"));
      return;
    }
    if (d.selectedCaptures.length === 2) {
      setMergeDialogOpen(true);
    } else {
      d.handleMergeCaptures(null);
    }
  };

  const activeTagName = isTrash
    ? t("mobile.trash")
    : d.selectedTagId === ALL_CAPTURES_FILTER
    ? t("mobile.showAll")
    : d.selectedTagId === TODAY_FILTER
    ? t("sidebar.today")
    : d.selectedTagId
    ? d.tags.find((t) => t.id === d.selectedTagId)?.name
    : t("mobile.untagged");

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-dashboard-page">
      <ModuleNavBar active="notes" />
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-xl border-b border-border px-4 py-4">
        <p className="font-mono-tag text-[10px] text-primary mb-0.5">NOTE</p>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-xl font-black tracking-tight" data-testid="mobile-view-title">{activeTagName}</h1>
          <div className="flex items-center gap-2">
            {!isTrash && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="mobile-sort-field-menu-button"
                      title={d.sortField === "created_at" ? t("dashboard.sortByCreated") : t("dashboard.sortByModified")}
                      className="h-8 w-8 flex items-center justify-center border border-border text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <CalendarBlank size={16} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none">
                    <DropdownMenuItem data-testid="mobile-sort-field-modified" onClick={() => d.setSortField("updated_at")} className="gap-2">
                      <ClockCounterClockwise size={14} /> {t("dashboard.sortByModified")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="mobile-sort-field-created" onClick={() => d.setSortField("created_at")} className="gap-2">
                      <CalendarBlank size={14} /> {t("dashboard.sortByCreated")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  data-testid="mobile-sort-order-toggle-button"
                  onClick={d.toggleSortOrder}
                  title={d.sortOrder === "desc" ? t("dashboard.sortNewestFirst") : t("dashboard.sortOldestFirst")}
                  className="h-8 w-8 flex items-center justify-center border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  {d.sortOrder === "desc" ? <SortDescending size={16} weight="bold" /> : <SortAscending size={16} weight="bold" />}
                </button>
              </>
            )}
          {isTrash ? (
            d.trashSelectedIds.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="mobile-trash-bulk-action-button" size="sm" className="rounded-none gap-1 bg-primary font-semibold">
                    {t("dashboard.action")}（{d.trashSelectedIds.length}） <CaretDown size={12} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none">
                  <DropdownMenuItem data-testid="mobile-trash-bulk-restore" onClick={d.handleBulkRestore} className="gap-2">
                    <ArrowCounterClockwise size={14} /> {t("dashboard.restore")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="mobile-trash-bulk-permanent-delete"
                    onClick={() => {
                      if (window.confirm(t("dashboard.confirmPermanentDeleteMany", { count: d.trashSelectedIds.length }))) {
                        d.handleBulkPermanentDelete();
                      }
                    }}
                    className="gap-2 text-destructive focus:text-destructive"
                  >
                    <Trash size={14} /> {t("dashboard.permanentDelete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )
          ) : (
            d.selectedIds.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="mobile-bulk-action-button" size="sm" className="rounded-none gap-1 bg-primary font-semibold">
                    {t("dashboard.action")}（{d.selectedIds.length}） <CaretDown size={12} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none">
                  <DropdownMenuItem data-testid="mobile-bulk-send" onClick={() => d.setSendEmailOpen(true)} className="gap-2">
                    <PaperPlaneTilt size={14} /> {t("dashboard.sendEmail")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="mobile-bulk-tag" onClick={() => d.setBulkTagOpen(true)} className="gap-2">
                    <TagIcon size={14} /> {t("dashboard.addTag")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="mobile-bulk-pdf" onClick={() => downloadCapturesPdf(d.selectedIds)} className="gap-2">
                    <FilePdf size={14} /> {t("dashboard.downloadPdf")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="mobile-bulk-link" onClick={() => d.handleGenerateLink(d.selectedIds)} className="gap-2">
                    <LinkSimple size={14} /> {t("dashboard.generateLink")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="mobile-bulk-merge" onClick={handleMergeClick} className="gap-2">
                    <Stack size={14} /> {t("dashboard.merge")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="mobile-bulk-delete" onClick={d.handleBulkDelete} className="gap-2 text-destructive focus:text-destructive">
                    <Trash size={14} /> {t("dashboard.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )
          )}
          </div>
        </div>
      </header>

      <main className="px-4 py-5">
        {isTrash ? (
          d.trashCaptures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-border" data-testid="mobile-empty-trash-state">
              <Recycle size={40} className="text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">{t("dashboard.emptyTrash")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mobile-trash-grid">
              {d.trashCaptures.map((c) => (
                <TrashCaptureCard
                  key={c.id}
                  capture={c}
                  selected={d.trashSelectedIds.includes(c.id)}
                  onToggleSelect={d.toggleTrashSelect}
                  onRestore={d.handleRestore}
                  onPermanentDelete={d.handlePermanentDelete}
                />
              ))}
            </div>
          )
        ) : d.captures.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-border" data-testid="mobile-empty-state">
            <ImageSquare size={40} className="text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">{t("mobile.emptyCaptures")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mobile-captures-grid">
            {d.captures.map((c) => (
              <CaptureCard
                key={c.id}
                capture={c}
                tags={d.tags}
                selected={d.selectedIds.includes(c.id)}
                onToggleSelect={d.toggleSelect}
                onOpen={d.setEditCapture}
                onDelete={d.handleDeleteCapture}
                onSendSingle={(cap) => {
                  d.setSingleSendCapture(cap);
                  d.setSendEmailOpen(true);
                }}
                onAddTag={d.setTagCapture}
                onGenerateLink={d.handleGenerateLink}
                onToggleFavorite={d.handleToggleFavorite}
              />
            ))}
          </div>
        )}
      </main>

      <MobileBottomNav activeTab={activeTab} onTabChange={handleTabChange} onUploadClick={() => setUploadSheetOpen(true)} />

      <Sheet open={tagsSheetOpen} onOpenChange={setTagsSheetOpen}>
        <SheetContent side="bottom" className="rounded-none max-h-[70vh] overflow-y-auto" data-testid="mobile-tags-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">{t("mobile.tagsSheetTitle")}</SheetTitle>
            <SheetDescription>{t("mobile.tagsSheetDescription")}</SheetDescription>
          </SheetHeader>
          <div className="space-y-1 mt-4">
            <button
              data-testid="mobile-tag-filter-show-all"
              onClick={() => {
                d.selectTag(ALL_CAPTURES_FILTER);
                setActiveTab("all");
                setTagsSheetOpen(false);
              }}
              className={`w-full text-left px-3 py-3 text-sm font-medium ${d.selectedTagId === ALL_CAPTURES_FILTER && !isTrash ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              {t("mobile.showAll")}
            </button>
            <button
              data-testid="mobile-tag-filter-today"
              onClick={() => {
                d.selectTag(TODAY_FILTER);
                setActiveTab("all");
                setTagsSheetOpen(false);
              }}
              className={`w-full text-left px-3 py-3 text-sm font-medium ${d.selectedTagId === TODAY_FILTER && !isTrash ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              {t("sidebar.today")}
            </button>
            <button
              data-testid="mobile-tag-filter-all"
              onClick={() => {
                d.selectTag(null);
                setActiveTab("all");
                setTagsSheetOpen(false);
              }}
              className={`w-full text-left px-3 py-3 text-sm font-medium ${!d.selectedTagId && !isTrash ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              {t("mobile.untagged")}
            </button>
            {d.folders.map((folder) => {
              const folderTags = d.tags.filter((tag) => tag.folder_id === folder.id);
              const isExpanded = expandedMobileFolderIds.includes(folder.id);
              return (
                <div key={folder.id}>
                  <button
                    data-testid={`mobile-folder-toggle-${folder.id}`}
                    onClick={() => setExpandedMobileFolderIds((prev) => (prev.includes(folder.id) ? prev.filter((id) => id !== folder.id) : [...prev, folder.id]))}
                    className="w-full text-left px-3 py-3 text-sm font-medium flex items-center gap-2 hover:bg-secondary"
                  >
                    {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                    <FolderSimple size={16} />
                    <span className="flex-1 truncate">{folder.name}</span>
                    <span className="font-mono-tag text-[10px] opacity-70">{folderTags.length}</span>
                  </button>
                  {isExpanded && (
                    <div className="pl-6">
                      {folderTags.map((tg) => (
                        <div key={tg.id} className="flex items-center gap-1">
                          <button
                            data-testid={`mobile-tag-item-${tg.id}`}
                            onClick={() => {
                              d.selectTag(tg.id);
                              setActiveTab("all");
                              setTagsSheetOpen(false);
                            }}
                            className={`flex-1 text-left px-3 py-3 text-sm font-medium flex items-center justify-between ${d.selectedTagId === tg.id && !isTrash ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
                          >
                            <span>{tg.name}</span>
                            <span className="font-mono-tag text-[10px] opacity-70">{tg.capture_count}</span>
                          </button>
                          <button data-testid={`mobile-tag-generate-link-${tg.id}`} onClick={() => d.handleGenerateTagLink(tg.id)} className="px-2 text-muted-foreground">
                            <LinkSimple size={16} />
                          </button>
                          <button data-testid={`mobile-tag-delete-${tg.id}`} onClick={() => d.handleDeleteTag(tg.id)} className="px-3 text-muted-foreground">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {d.tags.filter((tag) => !tag.folder_id).map((tg) => (
              <div key={tg.id} className="flex items-center gap-1">
                <button
                  data-testid={`mobile-tag-item-${tg.id}`}
                  onClick={() => {
                    d.selectTag(tg.id);
                    setActiveTab("all");
                    setTagsSheetOpen(false);
                  }}
                  className={`flex-1 text-left px-3 py-3 text-sm font-medium flex items-center justify-between ${d.selectedTagId === tg.id && !isTrash ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
                >
                  <span className="flex items-center gap-1.5">
                    {tg.is_favorites && <Star size={14} weight="fill" className={d.selectedTagId === tg.id && !isTrash ? "" : "text-amber-500"} />}
                    {tg.name}
                  </span>
                  <span className="font-mono-tag text-[10px] opacity-70">{tg.capture_count}</span>
                </button>
                <button data-testid={`mobile-tag-generate-link-${tg.id}`} onClick={() => d.handleGenerateTagLink(tg.id)} className="px-2 text-muted-foreground">
                  <LinkSimple size={16} />
                </button>
                {!tg.is_favorites && (
                  <button data-testid={`mobile-tag-delete-${tg.id}`} onClick={() => d.handleDeleteTag(tg.id)} className="px-3 text-muted-foreground">
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              data-testid="mobile-new-tag-button"
              onClick={() => {
                setTagsSheetOpen(false);
                d.setNewTagOpen(true);
              }}
              className="w-full text-left px-3 py-3 text-sm font-medium flex items-center gap-2 text-muted-foreground"
            >
              <Plus size={16} /> {t("mobile.newTag")}
            </button>
            <button
              data-testid="mobile-new-folder-button"
              onClick={() => {
                setTagsSheetOpen(false);
                d.setNewFolderOpen(true);
              }}
              className="w-full text-left px-3 py-3 text-sm font-medium flex items-center gap-2 text-muted-foreground"
            >
              <FolderPlus size={16} /> {t("mobile.newFolder")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={uploadSheetOpen} onOpenChange={setUploadSheetOpen}>
        <SheetContent side="bottom" className="rounded-none" data-testid="mobile-upload-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">{t("mobile.uploadSheetTitle")}</SheetTitle>
            <SheetDescription>{t("mobile.uploadSheetDescription")}</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-1 gap-2 mt-4">
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" data-testid="mobile-camera-input" className="hidden" onChange={(e) => { d.handleUploadFiles(e.target.files); e.target.value = ""; setUploadSheetOpen(false); }} />
            <Button data-testid="mobile-camera-button" onClick={() => cameraInputRef.current?.click()} className="rounded-none gap-2 justify-start bg-primary">
              <Camera size={18} weight="bold" /> {t("mobile.cameraUpload")}
            </Button>
            <input ref={galleryInputRef} type="file" accept="image/*,application/pdf" multiple data-testid="mobile-gallery-input" className="hidden" onChange={(e) => { d.handleUploadFiles(e.target.files); e.target.value = ""; setUploadSheetOpen(false); }} />
            <Button data-testid="mobile-gallery-button" variant="outline" onClick={() => galleryInputRef.current?.click()} className="rounded-none gap-2 justify-start">
              <UploadSimple size={18} weight="bold" /> {t("mobile.galleryUpload")}
            </Button>
            <Button
              data-testid="mobile-new-text-note-button"
              variant="outline"
              onClick={() => {
                setUploadSheetOpen(false);
                d.handleCreateTextNote();
              }}
              className="rounded-none gap-2 justify-start"
            >
              <NotePencil size={18} weight="bold" /> {t("dashboard.newTextNote")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={meSheetOpen} onOpenChange={setMeSheetOpen}>
        <SheetContent side="bottom" className="rounded-none" data-testid="mobile-me-sheet">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="font-display">{user?.name}</SheetTitle>
              <LanguageSwitcher />
            </div>
            <SheetDescription>{user?.email}</SheetDescription>
          </SheetHeader>
          <Button
            data-testid="mobile-trash-nav-button"
            variant="outline"
            onClick={() => {
              d.openTrash();
              setMeSheetOpen(false);
            }}
            className="w-full rounded-none gap-2 justify-start mt-2"
          >
            <Recycle size={16} /> {t("mobile.trash")}
          </Button>
          <Button
            data-testid="mobile-install-guide-button"
            variant="outline"
            onClick={() => window.open("/install", "_blank")}
            className="w-full rounded-none gap-2 justify-start mt-2"
          >
            <DeviceMobile size={16} /> {t("mobile.installToPhone")}
          </Button>
          <Button
            data-testid="mobile-logout-button"
            variant="outline"
            onClick={() => {
              logout();
              toast.success(t("mobile.logoutSuccess"));
            }}
            className="w-full rounded-none gap-2 justify-start mt-2"
          >
            <SignOut size={16} /> {t("mobile.logout")}
          </Button>
        </SheetContent>
      </Sheet>

      <CaptureEditDialog
        capture={d.editCapture}
        open={!!d.editCapture}
        onOpenChange={(o) => !o && d.setEditCapture(null)}
        onDelete={d.handleDeleteCapture}
        onSaved={d.handleCaptureSaved}
      />
      <AddTagDialog
        capture={d.tagCapture}
        tags={d.tags}
        open={!!d.tagCapture}
        onOpenChange={(o) => !o && d.setTagCapture(null)}
        onTagged={d.handleCaptureTagged}
        onCreateTag={(t) => d.setTags((prev) => [t, ...prev])}
      />
      <BulkAddTagDialog
        captureIds={d.selectedIds}
        tags={d.tags}
        open={d.bulkTagOpen}
        onOpenChange={d.setBulkTagOpen}
        onTagged={d.handleBulkTagged}
        onCreateTag={(t) => d.setTags((prev) => [t, ...prev])}
      />
      <NewTagDialog open={d.newTagOpen} onOpenChange={d.setNewTagOpen} onCreated={(t) => d.setTags((prev) => [t, ...prev])} />
      <NewFolderDialog open={d.newFolderOpen} onOpenChange={d.setNewFolderOpen} onCreated={(f) => d.setFolders((prev) => [f, ...prev])} />
      <ShareLinkDialog open={d.shareLinkOpen} onOpenChange={d.setShareLinkOpen} url={d.shareLinkUrl} />
      <MergeLayoutDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen} onChoose={d.handleMergeCaptures} />
      <SendEmailModal
        open={d.sendEmailOpen}
        onOpenChange={(o) => {
          d.setSendEmailOpen(o);
          if (!o) d.setSingleSendCapture(null);
        }}
        selectedCaptures={d.emailTargets}
        userEmail={user?.email}
        onSent={() => {
          d.setSelectedIds([]);
          d.setSingleSendCapture(null);
        }}
      />
    </div>
  );
}
